/**
 * Breadcrumb Component
 * Page hierarchy navigation with keyboard support
 */

import { Fragment } from 'react';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  separator?: 'slash' | 'chevron' | 'arrow';
  maxItems?: number;
  className?: string;
}

export function Breadcrumb({
  items,
  separator = 'chevron',
  maxItems,
  className = '',
}: BreadcrumbProps) {
  const { t } = useTranslation();

  const separatorIcons = {
    slash: (
      <span className="mx-2 text-gray-400" aria-hidden="true">
        /
      </span>
    ),
    chevron: (
      <svg
        className="mx-2 h-5 w-5 text-gray-400"
        fill="currentColor"
        viewBox="0 0 20 20"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
          clipRule="evenodd"
        />
      </svg>
    ),
    arrow: (
      <svg
        className="mx-2 h-5 w-5 text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5l7 7-7 7"
        />
      </svg>
    ),
  };

  // Handle collapsed breadcrumbs if maxItems is specified
  const displayItems = maxItems && items.length > maxItems
    ? [
        items[0],
        {
          label: '...',
          disabled: true,
        },
        ...items.slice(-(maxItems - 2)),
      ]
    : items;

  const handleKeyDown = (
    event: KeyboardEvent,
    item: BreadcrumbItem,
    index: number
  ) => {
    // Enter or Space to activate link
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!item.disabled && item.onClick) {
        item.onClick();
      }
    }

    // Arrow key navigation
    const links = document.querySelectorAll('[data-breadcrumb-item]');
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      (links[index - 1] as HTMLElement)?.focus();
    } else if (event.key === 'ArrowRight' && index < links.length - 1) {
      event.preventDefault();
      (links[index + 1] as HTMLElement)?.focus();
    } else if (event.key === 'Home') {
      event.preventDefault();
      (links[0] as HTMLElement)?.focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      (links[links.length - 1] as HTMLElement)?.focus();
    }
  };

  const isLastItem = (index: number) => index === displayItems.length - 1;

  return (
    <nav
      className={`flex ${className}`}
      aria-label={t('navigation.breadcrumb', 'Breadcrumb')}
    >
      <ol className="flex items-center space-x-0">
        {displayItems.map((item, index) => {
          const isLast = isLastItem(index);
          const isClickable = !item.disabled && !isLast && (item.href || item.onClick);

          return (
            <Fragment key={`${item.label}-${index}`}>
              <li className="flex items-center">
                {isClickable ? (
                  item.href ? (
                    <a
                      href={item.href}
                      data-breadcrumb-item
                      onClick={(e) => {
                        if (item.onClick) {
                          e.preventDefault();
                          item.onClick();
                        }
                      }}
                      onKeyDown={(e) => handleKeyDown(e, item, index)}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded px-1"
                      aria-current={isLast ? 'page' : undefined}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <button
                      type="button"
                      data-breadcrumb-item
                      onClick={item.onClick}
                      onKeyDown={(e) => handleKeyDown(e, item, index)}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded px-1"
                      aria-current={isLast ? 'page' : undefined}
                    >
                      {item.label}
                    </button>
                  )
                ) : (
                  <span
                    data-breadcrumb-item
                    className={`text-sm font-medium px-1 ${
                      isLast
                        ? 'text-gray-700'
                        : item.disabled
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-500'
                    }`}
                    aria-current={isLast ? 'page' : undefined}
                    tabIndex={item.disabled ? -1 : 0}
                    onKeyDown={
                      !item.disabled ? (e) => handleKeyDown(e, item, index) : undefined
                    }
                  >
                    {item.label}
                  </span>
                )}
              </li>
              {!isLast && (
                <li className="flex items-center" aria-hidden="true">
                  {separatorIcons[separator]}
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
